import React from 'react';
import { Navigation, AlertCircle, ExternalLink } from 'lucide-react';
import { Venue } from '@/types';
import { getGoogleMapsUrl, getVenueMapUrl } from '@/lib/venueMaps';

interface VenuePickerProps {
  venues: Venue[];
  selectedVenueId: string | null;
  distances: Record<string, number>;
  userPostcode: string;
  isSearchingPostcode: boolean;
  postcodeError: string;
  onPostcodeChange: (value: string) => void;
  onFindNearest: () => void;
  onVenueSelect: (venueId: string) => void;
}

export const VenuePicker: React.FC<VenuePickerProps> = ({
  venues,
  selectedVenueId,
  distances,
  userPostcode,
  isSearchingPostcode,
  postcodeError,
  onPostcodeChange,
  onFindNearest,
  onVenueSelect
}) => {
  const sortedVenues = [...venues].sort(
    (a, b) => (distances[a.id] ?? Infinity) - (distances[b.id] ?? Infinity)
  );

  return (
    <>
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-6 flex flex-col sm:flex-row items-center gap-3">
        <div className="flex-1 w-full relative">
          <Navigation className="absolute left-3 top-2.5 w-5 h-5 text-slate-400" />
          <input
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 outline-none bg-slate-50"
            placeholder="Enter UK Postcode..."
            value={userPostcode}
            onChange={(e) => onPostcodeChange(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && onFindNearest()}
          />
        </div>
        <button
          onClick={onFindNearest}
          disabled={isSearchingPostcode || !userPostcode}
          className="bg-slate-900 text-white px-5 py-2 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 w-full sm:w-auto"
        >
          {isSearchingPostcode ? 'Finding...' : 'Find Nearest'}
        </button>
      </div>
      {postcodeError && (
        <div className="mb-6 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {postcodeError}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {sortedVenues.map((v) => {
          const mapUrl = getVenueMapUrl(v, { zoom: 13 });
          const mapsLink = getGoogleMapsUrl(v);
          const imageSrc =
            mapUrl || v.image || 'https://placehold.co/640x360/e2e8f0/64748b?text=No+Map';
          return (
            <div
              key={v.id}
              onClick={() => onVenueSelect(v.id)}
              className={`border rounded-xl overflow-hidden cursor-pointer transition-all hover:shadow-lg transform hover:-translate-y-1 relative ${selectedVenueId === v.id ? 'ring-2 ring-teal-500 border-teal-500' : 'border-slate-200'}`}
            >
              {distances[v.id] !== undefined && (
                <div className="absolute top-2 right-2 bg-slate-900/90 text-white text-[10px] font-black px-2 py-1 rounded shadow-md z-10">
                  {distances[v.id]} MILES
                </div>
              )}
              <div className="relative">
                <img
                  src={imageSrc}
                  alt={v.name}
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
                {mapsLink && (
                  <a
                    href={mapsLink}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Open ${v.name} in Google Maps`}
                    className="absolute bottom-2 left-2 inline-flex items-center gap-1.5 bg-white/90 text-slate-700 text-[10px] font-bold px-2 py-1 rounded-full shadow-sm"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Open in Maps
                  </a>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-slate-900">{v.name}</h3>
                <p className="text-slate-500 text-xs mt-1">{v.address}</p>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};
